import React from "react";
import Modal from "react-modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    border: "none",
    background: "transparent",
    padding: "0",
    maxWidth: "min(500px, 92vw)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1100,
  },
};

/** Header shows `color_name` + " Line"; skip the clause when storage only had a hex code. */
function formatAdminLine(colorLine) {
  const raw = String(colorLine || "").trim();
  if (!raw || raw.startsWith("#")) return "";
  return /line$/i.test(raw) ? raw : `${raw} Line`;
}

/**
 * Member flow: choose Mother / Father side (same options as the header family-side dropdown).
 * No dismiss without a selection — matches required choice from the old dropdown.
 */
export default function SelectFamilySideModal({
  isOpen,
  isLoading,
  /** One row per API tree: `{ treeId, familySide, label, creatorName }` — label is `creator_name (Mother's Side | Father's Side | …)` for display */
  options,
  /** Current tree id from storage / session (highlights the right row when sides match). */
  selectedTreeId,
  /** Unspaced top-of-the-line title, e.g. "Witherspoon & Patterson Family". */
  familyName,
  /** Family admin name from `admin_details` — fallback when a row has no `creator_name`. */
  adminName,
  /** `admin_details.color_name`, e.g. "Red" (rendered as "Red Line"). */
  adminColorLine,
  onSelect,
}) {
  /** Admin sentence only makes sense for a single line — with several, the user still has to pick one. */
  const onlyOption = options && options.length === 1 ? options[0] : null;
  const admin = (onlyOption?.creatorName || adminName || "").trim();
  // Sentence below reads better with just the first name; the button keeps the full name.
  const adminFirstName = admin.split(/\s+/)[0] || "";
  const adminLine = formatAdminLine(adminColorLine);
  const familyLabel = String(familyName || "").trim();
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => {}}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      style={customStyles}
      contentLabel="Select which family side to view your family tree"
      ariaHideApp={false}
    >
      <div
        className="modal-content"
        style={{
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          background: "#fff",
          padding: "1.35rem 1.25rem 1.25rem",
        }}
      >
        <h2
          style={{
            margin: "0 0 1rem",
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.45,
            color: "#111827",
            textAlign: "center",
            // Even out the line lengths so no word ends up alone on the last line.
            textWrap: "balance",
          }}
        >
          <span style={{ display: "block" }}>
            {familyLabel
              ? `You are now part of the ${familyLabel.replace(/ (\S+)$/, "\u00A0$1")}.`
              : "You are now part of this family."}
          </span>
          {admin ? (
            <span style={{ display: "block" }}>
              {`${adminFirstName}${adminLine ? ` in the ${adminLine}` : ""} is the admin for this family as noted in the button below. Click on the button to\u00A0proceed.`}
            </span>
          ) : null}
          {options && options.length > 1 ? (
            <span style={{ display: "block" }}>
              Select which side to view your family tree.
            </span>
          ) : null}
        </h2>
        {isLoading ? (
          <p style={{ margin: 0, textAlign: "center", color: "#6b7280" }}>
            Loading…
          </p>
        ) : !options || options.length === 0 ? (
          <p style={{ margin: 0, textAlign: "center", color: "#6b7280" }}>
            No family lines available.
          </p>
        ) : (
          <div
            className="select-family-side-modal-list"
            role="listbox"
            aria-label="Families you can view"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
              /* ~6 rows visible (row minHeight 3rem + gap), then scroll */
              maxHeight: "calc(6 * 3rem + 5 * 0.5rem)",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              scrollbarGutter: "stable",
            }}
          >
            {options.map(({ treeId, familySide, label }) => {
              const selected =
                selectedTreeId != null &&
                String(selectedTreeId) === String(treeId);
              return (
                <button
                  key={treeId}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`select-family-side-modal-item${selected ? " is-selected" : ""}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    minHeight: "3rem",
                    flexShrink: 0,
                    boxSizing: "border-box",
                    padding: "0.75rem 1rem",
                    borderRadius: "8px",
                    border: selected ? "2px solid #2563eb" : "1px solid #e5e7eb",
                    background: selected ? "#eff6ff" : "#f9fafb",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    color: "#111827",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onClick={() => onSelect({ treeId, familySide })}
                >
                  <span>{label}</span>
                  {selected ? (
                    <FontAwesomeIcon icon={faCheck} style={{ color: "#2563eb" }} />
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
